import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocalServer } from '../../context/LocalServerContext';
import { useAuth } from '../../context/AuthContext';
import { downloadFile, downloadErrorMessage } from '../../services/api';

// Only management roles get prompted to download/run an installer on the
// restaurant's PC — a PIN-authenticated cashier/server shouldn't see this.
const ALLOWED_ROLES = ['manager', 'partner', 'admin', 'super_admin'];

/**
 * Persistent button shown once the cloud has a newer local-server build than the
 * currently connected instance (see LocalServerContext's throttled 1x/day check).
 * Clicking downloads the installer — a browser can't execute it automatically,
 * the manager still has to double-click it on the restaurant's PC. The NSIS
 * installer already detects an existing install and updates in place without
 * asking for a new setup token (see local-server/build-installer.ps1).
 */
export default function LocalServerUpdateButton() {
    const { updateAvailable } = useLocalServer();
    const { user } = useAuth();

    if (!updateAvailable || !user || !ALLOWED_ROLES.includes(user.role)) return null;

    const handleDownload = async () => {
        try {
            await downloadFile(`/local-sync/download-build/${updateAvailable.filename}`, updateAvailable.filename);
            toast.success('Téléchargement lancé — exécutez le fichier pour mettre à jour le serveur local.');
        } catch (err) {
            toast.error(downloadErrorMessage(err, 'Échec du téléchargement de la mise à jour.'));
        }
    };

    return (
        <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
            title={`Mise à jour du serveur local disponible (v${updateAvailable.version})`}
        >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden 2xl:inline">Mise à jour disponible (v{updateAvailable.version})</span>
        </button>
    );
}
